import React, { useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Loading,
  Toggle,
  InlineNotification,
  Tag,
} from '@carbon/react';

import { useGetGroupsQuery } from '@/redux/new_services/groups';
import { useGetHolidaysQuery } from '@/redux/new_services/holidays';
import { useGetHolidayVariantsQuery } from '@/redux/new_services/holidayVariants';
import {
  useGetGroupHolidaysQuery,
  useCreateGroupHolidayMutation,
  useUpdateGroupHolidayMutation,
  useDeleteGroupHolidayMutation,
} from '@/redux/new_services/groupHolidays';

import './GroupHolidaysPage.scss';

// GroupHolidaysPage lets the admin choose a security group and decide which
// holidays apply to that group plus which variant should be used. The
// underlying group_holidays row is created/deleted via the toggle and
// updated via the variant dropdown.
const GroupHolidaysPage = () => {
  const { data: groupsResp, isLoading: groupsLoading } = useGetGroupsQuery();
  const groups = groupsResp?.data || [];
  const [groupId, setGroupId] = useState(null);

  const { data: holidays = [], isLoading: holidaysLoading } = useGetHolidaysQuery();
  const { data: groupHolidays = [], isLoading: ghLoading } = useGetGroupHolidaysQuery(groupId, {
    skip: !groupId,
  });

  const [createGroupHoliday] = useCreateGroupHolidayMutation();
  const [updateGroupHoliday] = useUpdateGroupHolidayMutation();
  const [deleteGroupHoliday] = useDeleteGroupHolidayMutation();

  // Index group_holidays by holiday_id for fast lookup in the table render.
  const byHolidayId = useMemo(() => {
    const map = {};
    for (const gh of groupHolidays) map[gh.holiday_id] = gh;
    return map;
  }, [groupHolidays]);

  return (
    <div className="group-holidays-page">
      <h2>Feiertage pro Gruppe</h2>

      <p className="group-holidays-page__lead">
        Wähle eine Gruppe und entscheide, welche Feiertage für sie gelten.
        Pro aktiviertem Feiertag legst du fest, welche Medien-Variante
        angezeigt werden soll. Die Liste der Feiertage selbst und ihrer
        Varianten verwaltest du unter „Feiertage / Stammdaten".
      </p>

      <Dropdown
        id="group-picker"
        titleText="Gruppe wählen"
        label="Gruppe auswählen"
        items={groups}
        itemToString={(g) => (g ? g.name : '')}
        onChange={({ selectedItem }) => setGroupId(selectedItem?.model?.ID || null)}
      />

      {(groupsLoading || holidaysLoading || (groupId && ghLoading)) && (
        <Loading active small withOverlay={false} />
      )}

      {!groupId && (
        <InlineNotification
          kind="info"
          title="Hinweis"
          subtitle="Wähle oben eine Gruppe, um die Feiertags-Konfiguration zu sehen."
          hideCloseButton
          lowContrast
        />
      )}

      {groupId && holidays.length === 0 && !holidaysLoading && (
        <InlineNotification
          kind="info"
          title="Keine Feiertage angelegt"
          subtitle={'Lege zuerst Feiertags-Stammdaten unter „Feiertage → Stammdaten" an.'}
          hideCloseButton
          lowContrast
        />
      )}

      {groupId && holidays.length > 0 && (
        <ul className="group-holidays-page__list">
          {holidays.map((h) => (
            <HolidayRow
              key={h.model.ID}
              holiday={h}
              groupId={groupId}
              binding={byHolidayId[h.model.ID]}
              onActivate={() =>
                createGroupHoliday({ groupId, holiday_id: h.model.ID })
              }
              onDeactivate={(binding) =>
                deleteGroupHoliday({ id: binding.model.ID, groupId })
              }
              onChangeVariant={(binding, variantId) =>
                updateGroupHoliday({
                  id: binding.model.ID,
                  groupId,
                  variant_id: variantId,
                })
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
};

// Single row for one holiday: toggle on/off + variant dropdown when active.
// Lazily fetches variants only for active rows so the page doesn't fan out
// to one variants-query per holiday on first render.
const HolidayRow = ({ holiday, binding, onActivate, onDeactivate, onChangeVariant }) => {
  const isActive = Boolean(binding);
  const { data: variants = [] } = useGetHolidayVariantsQuery(holiday.model.ID, {
    skip: !isActive,
  });

  const selectedVariant = isActive
    ? variants.find((v) => v.model.ID === binding.variant_id) || null
    : null;

  return (
    <li className="group-holidays-page__row">
      <div className="group-holidays-page__row-head">
        <Toggle
          id={`holiday-toggle-${holiday.model.ID}`}
          labelText=""
          labelA="Inaktiv"
          labelB="Aktiv"
          toggled={isActive}
          onToggle={(checked) => {
            if (checked) onActivate();
            else onDeactivate(binding);
          }}
        />
        <span className="group-holidays-page__row-name">{holiday.name}</span>
        {(holiday.federal_states || []).map((s) => (
          <Tag key={s.code} type="cool-gray" size="sm">
            {s.code}
          </Tag>
        ))}
      </div>

      {isActive && (
        <div className="group-holidays-page__row-variant">
          <Dropdown
            id={`variant-picker-${holiday.model.ID}`}
            titleText="Variante"
            label="Variante wählen"
            items={variants}
            itemToString={(v) => (v ? `${v.name}${v.is_default ? ' (Standard)' : ''}` : '')}
            selectedItem={selectedVariant}
            onChange={({ selectedItem }) =>
              onChangeVariant(binding, selectedItem?.model?.ID || null)
            }
          />
        </div>
      )}
    </li>
  );
};

export default GroupHolidaysPage;
